import { Canvas } from "@react-three/fiber";
import { motion, useScroll, useTransform } from "motion/react";
import { Suspense, useMemo, useRef } from "react";
import { cn } from "../../lib/utils";
import { CTA } from "../blog/CTA";
import { WordMark } from "../brand/WordMark";
import AsciiColorGradient from "../three/scene";
import { ComplianceBadge } from "../ui/ComplianceBadge";

export function CoplaneFooter() {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Track scroll progress relative to this section
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end end"],
  });

  // Header fade in
  const headerY = useTransform(scrollYProgress, [0, 0.2], [50, 0]);
  const headerOpacity = useTransform(scrollYProgress, [0, 0.2], [0, 1]);

  // Bars animation
  // We want them to fan out or stack. 
  // Let's have them start stacked and spread out as we scroll down.
  const bars = Array.from({ length: 1 }); // Just one bar for now
  // const bars = Array.from({ length: 6 }); // 6 bars including the top one
  
  // Ascii Gradient Props - simplified for footer
  const gradientProps = useMemo(() => {
    return {
      controls: {
        animSpeed: 0.66,
      },
      asciiControls: {
        asciiEnabled: true,
        asciiInvert: false,
        asciiGamma: .3,
        asciiContrast: .5,
        asciiBrightness: -0.2,
        asciiRangeMin: .05,
        asciiRangeMax: .85,
        asciiActiveCount: 40,
        asciiShift: 8,
        asciiSwapColors: false,
        asciiPrimaryColor: { r: 128, g: 128, b: 128 },
        asciiSecondaryColor: { r: 35, g: 31, b: 32 },
      },
    };
  }, []);

  return (
    <footer
      ref={containerRef}
      className="relative w-full bg-black pt-20 pb-10 overflow-hidden flex items-center justify-center z-[1000]" // High z-index on footer itself
      style={{ minHeight: "100vh" }}
    >
      {/* Ascii Background */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <Canvas dpr={[1, 1.5]} gl={{ antialias: true }}>
          <Suspense fallback={null}>
            <AsciiColorGradient
              animSpeed={gradientProps.controls.animSpeed}
              asciiEnabled={gradientProps.asciiControls.asciiEnabled}
              asciiInvert={gradientProps.asciiControls.asciiInvert}
              asciiGamma={gradientProps.asciiControls.asciiGamma}
              asciiContrast={gradientProps.asciiControls.asciiContrast}
              asciiBrightness={gradientProps.asciiControls.asciiBrightness}
              asciiRangeMin={gradientProps.asciiControls.asciiRangeMin}
              asciiRangeMax={gradientProps.asciiControls.asciiRangeMax}
              asciiActiveCount={gradientProps.asciiControls.asciiActiveCount}
              asciiShift={gradientProps.asciiControls.asciiShift}
              asciiPrimaryColor={gradientProps.asciiControls.asciiPrimaryColor}
              asciiSecondaryColor={gradientProps.asciiControls.asciiSecondaryColor}
              asciiSwapColors={gradientProps.asciiControls.asciiSwapColors}
            />
          </Suspense>
        </Canvas>
        {/* Squircle Fade Overlay */}
        <div className="absolute inset-0 z-10 pointer-events-none">
             <div className="absolute inset-0 bg-radial-gradient from-transparent via-black/60 to-black blur-3xl" 
                  style={{ background: 'radial-gradient(circle at center, transparent 30%, black 90%)' }} 
             />
        </div>
      </div>

      {/* Hide Nav/Logo - This overlay effectively hides anything fixed under it by covering the viewport if needed,
          but specifically "hide the logo and the nav bar" usually implies hiding the FIXED header components 
          when this footer is visible.
          Since the header is fixed, we can't easily hide it from INSIDE the footer component without context.
          However, we can make the footer content sit on top of everything with a high z-index background.
          The user said "in the footer... hide the logo and nav bar".
          A common trick is to use a context or just cover it.
          Let's assume "hide the logo and the nav bar" means physically cover them or use the existing `hideHamburgerUntil` prop 
          if we had access to it (which we don't easily here).
          
          Actually, the footer has `relative z-20` and a black background. If it's full height (`min-h-[100vh]`), 
          it covers the document flow.
          BUT the header is `fixed z-60`.
          To cover the fixed header, the footer needs `z-[70]` or higher.
      */}
      
      <div className="relative z-[100] w-full"> {/* High z-index to cover fixed header */}
        {/* Top Headers */}
        <motion.div 
            className="mb-24 grid w-full max-w-[1400px] mx-auto grid-cols-1 gap-12 pb-8 text-center text-4xl font-semibold tracking-tight text-white md:grid-cols-3 md:gap-8 lg:text-6xl px-6 lg:px-8"
            style={{ y: headerY, opacity: headerOpacity }}
        >
            <div className="flex justify-center">Get</div>
            <div className="flex justify-center">CoPlane</div>
            <div className="flex justify-center">Now</div>
        </motion.div>

        {/* Stack of Bars */}
        <div className="relative w-full max-w-[1400px] mx-auto px-4 mb-8 perspective-[1000px]">
            <div className="flex flex-col items-center gap-2 md:gap-4 relative" style={{ perspective: "1000px" }}>
                {bars.map((_, i) => (
                    <FooterBar key={i} index={i} progress={scrollYProgress} total={bars.length} />
                ))}
            </div>
        </div>

        {/* Bottom Wordmark */}
        <div className="w-full max-w-[1400px] mx-auto px-4 mt-12 flex flex-col items-center">
            {/* WordMark width set to 100% to match the black footer bars width (since both are in max-w-[1400px] container with px-4) */}
            <WordMark className="w-full text-white" height="auto" />
            <p className="mt-6 text-center text-sm md:text-base font-medium text-white/80">
                Scale Intelligence, Not Headcount
            </p>
            
            {/* Compliance Badge */}
            <div className="mt-8">
              <ComplianceBadge />
            </div>
        </div>
      </div>
    </footer>
  );
}

function FooterBar({ index, progress, total }: { index: number; progress: any; total: number }) {
  // Since we only have 1 bar now, 'total' is 1.
  // If we divide by (total - 1), we get division by zero if total=1.
  // Let's just fix startRotation for the single bar case or handle the logic gracefully.
  
  // For a single bar, we still want it to rotateX from something like 45deg to 0deg.
  const startRotation = 45; 
  
  const rotateX = useTransform(
      progress,
      [0.2, 0.8],
      [startRotation, 0]
  );

  // Spring-like Y offset: spread them out as they rotate down
  const yOffset = useTransform(
      progress,
      [0.2, 0.8],
      [index * -20, 0]
  );

  const isFirst = index === 0;

  return (
    <motion.div
      className={cn(
        "w-full bg-white flex items-center justify-center relative origin-center will-change-transform",
        isFirst ? "min-h-[500px] flex-col p-8 md:p-12" : "h-12 md:h-16 lg:h-20"
      )}
      style={{
          marginTop: index === 0 ? 0 : "-1.5rem", 
          y: yOffset,
          zIndex: total - index,
          rotateX: rotateX,
      }}
    >
      {isFirst && (
        <>
           {/* CTA Container - centered/grow */}
           <div className="flex-1 flex items-center justify-center w-full">
             <CTA 
                title="Ready to scale?" 
                description="Let's build your autonomous back office." 
                buttonText="Request Access" 
                variant="secondary"
                className="my-0 border-none bg-transparent p-0 md:p-0 w-full max-w-2xl"
             />
           </div>
           
           {/* Links - at bottom */}
           <div className="flex w-full justify-center gap-8 md:gap-16 text-[10px] md:text-xs font-bold tracking-widest text-black uppercase mt-8">
             <a href="/blog" className="hover:text-gray-600 transition-colors">Blog</a>
             <a href="https://x.com/coplane" target="_blank" rel="noreferrer" className="hover:text-gray-600 transition-colors">X.com</a>
             <a href="https://github.com/coplane" target="_blank" rel="noreferrer" className="hover:text-gray-600 transition-colors">Github</a>
             <span className="hidden md:inline text-black/50">© 2025 CoPlane, Inc.</span>
           </div>
        </>
      )}
    </motion.div>
  );
}
